import type { UIViewInjectedProps } from '@uirouter/react';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';

import { api } from '@/api';
import { DrawerForm } from '@/components/drawer-form';
import { getAllowedFormFields } from '@/components/generic-form/utils';
import { PERMISSION_OBJ_CONSORTIUM } from '@/constants/permissions';
import { consortiumStore } from '@/stores';
import { getTranslatedString } from '@/utils';
import { withoutRepeatValue } from '@/utils/controller';

import { getFormFields } from './setup';

const store = consortiumStore;

export const ConsortiumsForm = observer(({ transition }: UIViewInjectedProps) => {
  const isCreate = transition.router.globals.current.name === 'base-layout.consortiums.create';
  const { consortiumId } = transition.router.globals.params;
  const foundItem = store.items.find((item: cyrian.api.consortiums.Consortium) => item.id === +consortiumId);

  useEffect(() => {
    if (!isCreate && consortiumId && !foundItem) {
      api.consortiums
        .get(+consortiumId)
        .source.then(({ data }) => store.addItem(data))
        .catch(() => {});
    }
  }, [isCreate, consortiumId, foundItem]);

  const onClose = () => {
    transition.router.stateService.go('base-layout.consortiums');
  };

  const initValues =
    !isCreate && foundItem
      ? {
          name: foundItem.name,
          cooperativeIds: foundItem.cooperatives?.map((c) => c.id) ?? [],
        }
      : {};

  const title = isCreate
    ? getTranslatedString('consortiums.create-new')
    : getTranslatedString('consortiums.consortium-number', { 0: consortiumId });

  const formFields = getFormFields();
  let allowedFields = getAllowedFormFields(formFields, isCreate, PERMISSION_OBJ_CONSORTIUM);
  allowedFields = allowedFields.map((field: any) => {
    if (field.name === 'cooperativeIds' && foundItem?.cooperatives) {
      return {
        ...field,
        params: {
          ...field.params,
          initialSelectedOptions: foundItem.cooperatives.map((c) => ({
            value: c.id,
            label: c.name,
          })),
        },
      };
    }
    return field;
  });

  const resourceController = withoutRepeatValue(initValues, {
    create: (values: any) => {
      const payload: cyrian.api.consortiums.ConsortiumCreateParams = {
        name: values.name,
        cooperativeIds: values.cooperativeIds,
      };
      return api.consortiums.create(payload).source.then(({ data }) => {
        store.addItem(data);
        return data;
      });
    },
    update: (values: any) => {
      const payload: cyrian.api.consortiums.ConsortiumUpdateParams = {
        name: values.name,
        cooperativeIds: values.cooperativeIds,
      };
      return api.consortiums.update(foundItem.id, payload).source.then(({ data }) => {
        store.updateItem(data);
        return data;
      });
    },
  });

  return (
    <DrawerForm
      title={title}
      initValues={initValues}
      formFields={allowedFields}
      onClose={onClose}
      resourceId={!isCreate && foundItem ? foundItem.id : undefined}
      resourceController={resourceController}
      loaderCondition={!isCreate && !foundItem}
    />
  );
});
